import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { ObjectId } from 'bson';
import { MenuItem } from 'primeng/api';
import { FormComponent } from '../../shared/components/form/form.component';
import type { NgxForm } from '../../shared/components/form/ngx-form.types';
import { OverlayService } from '../../shared/services/overlay.service';
import { RagDatasupplyFrontendService } from '@reflact/rag-datasupply';
import { FullUser, GlobalFrontendUser } from '@reflact/reflactportal';
import { ToastrService } from 'ngx-toastr';
import { MandantService } from 'src/app/shared/services/mandant.service';
import { TileService } from 'src/app/shared/services/tile.service';
import { UserService } from 'src/app/shared/services/user.service';
import { Tile } from 'src/app/shared/types/tile.types';
import { Mandant } from 'src/app/shared/types/mandant.types';
import { GlobalUserAdministrationComponent } from '../global-user-administration/global-user-administration.component';

/** MenuItem des "+"-Dropdowns fürs Kachel-Hinzufügen — transportiert das volle Tile-Objekt fürs
 * Custom-Item-Template (Icon + Akzentfarbe), siehe getTileMenuItems(). */
interface TileMenuItem extends MenuItem {
  tile: Tile;
}

/** Ein Eintrag im "Nutzer hinzufügen"-ng-select - entweder ein BESTEHENDER globaler Nutzer
 * (`isNew: false`, Name kommt von Logto und ist nicht editierbar) oder ein per Freitext
 * eingegebener, noch NICHT existierender Nutzer (`isNew: true`, Name muss separat erfasst
 * werden) - siehe addUserToMandant()/createNewUserOption(). */
interface UserOption {
  _id: string;
  email: string;
  name: string;
  isNew: boolean;
}

@Component({
  templateUrl: './mandant-administration.component.html',
  styleUrls: ['./mandant-administration.component.scss'],
  providers: [RagDatasupplyFrontendService],
  standalone: false
})
export class MandantAdministrationComponent implements AfterViewInit, OnInit {
  /** Optional (nicht `!`): analog zu `editFormRef` - beim ersten Rendern des Footers ist die
   * ViewChild-Referenz noch nicht gesetzt, ein Zugriff ohne Optional Chaining im Template wirft
   * dann eine Exception und lässt den Speichern-Button leer (siehe Template). */
  @ViewChild('formNew') public formNew?: FormComponent;
  /** Zeilenmarkierung in der p-table — unabhängig vom Bearbeiten-Drawer, siehe closeEditMandant(). */
  public selectedMandant?: Mandant;
  /** Steuert den Bearbeiten-Drawer (siehe selectMandantForEdit()/closeEditMandant()). */
  public editVisible = false;
  public editMandant?: Mandant;
  @ViewChild('form') public editFormRef?: FormComponent;
  /** F\u00fcr disableAutofillOnUserSelect() - Angular liest das als ElementRef des `<ng-select>`-Host-
   * Elements, weil `NgSelectComponent` keinen `@ViewChild`-fähigen Typ exportiert, den wir hier
   * importieren m\u00fcssten. */
  @ViewChild('userSelect', { read: ElementRef }) public userSelectRef?: ElementRef<HTMLElement>;
  /** Steuert das kurze visuelle Feedback am Speichern-Button (statt den Drawer zu schließen),
   * siehe updateMandant(). */
  public mandantSaveFeedback: 'idle' | 'saved' = 'idle';
  public mandantSaving = false;
  public createMandantSaving = false;
  /** Alle globalen Nutzer als ng-select-Optionen (`isNew: false`) - Basis f\u00fcrs "Nutzer
   * hinzuf\u00fcgen"-Dropdown im Bearbeiten-Drawer, siehe addUserToMandant(). */
  public userOptions: UserOption[] = [];
  /** Ausgew\u00e4hlte oder per Freitext neu eingegebene E-Mail im "Nutzer hinzuf\u00fcgen"-ng-select. */
  public selectedUserOption?: UserOption;
  /** Nur relevant, wenn `selectedUserOption?.isNew` - der Name ist bei bestehenden Nutzern
   * bereits bekannt (aus Logto) und daher nicht editierbar. */
  public newMandantUserName = '';
  /** Nutzer, die diesem Mandanten bereits angehören - siehe selectMandantForEdit()/
   * removeUserFromMandant(). */
  public mandantUsers: FullUser[] = [];
  public editingGlobalUserId?: string;
  @ViewChild('globalUserEditor') public globalUserEditor?: GlobalUserAdministrationComponent;
  private readonly destroyRef = inject(DestroyRef);

  public openUserDialog(user: FullUser): void {
    this.editingGlobalUserId = user.globalUserId.toString();
  }

  public closeUserDialog(): void {
    if (!this.globalUserEditor?.embeddedSaving) this.editingGlobalUserId = undefined;
  }

  public async onUserSaved(): Promise<void> {
    const mandant = this.editMandant;
    try {
      const [members, users] = await Promise.all([
        mandant ? this.userService.getMandantUsersAsReflactAdmin(mandant._id.toString()) : Promise.resolve([]),
        this.userService.getGlobalUsers()
      ]);
      if (this.destroyRef.destroyed) return;
      if (this.editMandant === mandant) this.mandantUsers = members;
      this.userOptions = users.map(user => ({ _id: user._id.toString(), email: user.email, name: user.name, isNew: false }));
    } catch {
      this.toastrService.error($localize`:@@mandantUserRefreshFailed:Die Nutzerliste konnte nicht aktualisiert werden.`);
    } finally {
      if (!this.destroyRef.destroyed) this.cdr.detectChanges();
    }
  }
  /** Steuert den "Neuer Mandant"-Drawer (siehe open()/closeCreateMandant()). */
  public createVisible = false;
  public newMandant?: { name: string; short: string; tiles: string[]; internal: boolean };
  /** Steuert das kurze visuelle Feedback am Speichern-Button des Erstellen-Drawers, siehe
   * createMandant(). */
  public createMandantSaveFeedback: 'idle' | 'saved' = 'idle';
  public translatedLabels: NgxForm.TranslatedLabels = new Map<string, string>([
    ["name", $localize`:@@globalName:Name`],
    ["short", $localize`:@@mandantAdminShort:Kürzel`],
    ["internal", $localize`:@@mandantAdminInternal:Interner Mandant (kein Kunde)`],
    ["tiles", $localize`:@@mandantAdminTiles:Kachel-Pool`]
  ])
  public tiles: Tile[] = [];

  constructor(public frontendService: RagDatasupplyFrontendService<Mandant>, public mandantService: MandantService, private toastrService: ToastrService, private overlayService: OverlayService, public tileService: TileService, private userService: UserService, private cdr: ChangeDetectorRef) {
    this.tileService.listTiles().then(m => {
      this.tiles = m;
      if (!this.destroyRef.destroyed) this.cdr.detectChanges();
    });
    this.mandantService.getMandants().then(m => this.frontendService.fromArray(m));
    this.userService.getGlobalUsers().then((users: GlobalFrontendUser[]) => {
      this.userOptions = users.map(u => ({ _id: u._id.toString(), email: u.email, name: u.name, isNew: false }));
    });
  }

  /** `ng-select`s `addTag`-Hook: wird aufgerufen, wenn die eingetippte E-Mail zu KEINEM
   * bestehenden Nutzer passt - liefert die neue Option, die `ng-select` dann automatisch auswählt.
   * Arrow-Function-Property (nicht Methode), weil `ng-select` sie ohne `this`-Bindung aufruft. */
  public createNewUserOption = (email: string): UserOption => ({ _id: `new:${email}`, email, name: '', isNew: true });

  /** `ng-select`s internes `<input>` wird von Passwortmanagern (Bitwarden, ...) sonst als Login-/
   * E-Mail-Feld erkannt und deren eigenes Overlay verdeckt dann ng-selects Dropdown - `ng-select`
   * bietet in dieser Version keine Möglichkeit, `autocomplete` selbst zu setzen, daher hier per
   * DOM-Zugriff beim Öffnen des Dropdowns. */
  public disableAutofillOnUserSelect(): void {
    const input = this.userSelectRef?.nativeElement.querySelector('input');
    input?.setAttribute('autocomplete', 'off');
    input?.setAttribute('data-lpignore', 'true');
    input?.setAttribute('data-1p-ignore', 'true');
  }

  ngOnInit(): void {
  }

  public async updateMandant(data: Mandant) {
    // Guards against a stray duplicate submit reaching the backend with an incomplete/empty payload.
    if (!data?._id) return;
    this.mandantSaving = true;
    this.cdr.detectChanges();
    let res;
    try {
      res = await this.mandantService.editManant(data._id, data.name, data.tiles, data.internal === true);
    } finally {
      this.mandantSaving = false;
      this.cdr.detectChanges();
    }
    if (res.status == "ok") {
      this.editMandantSnapshot = JSON.stringify(data);
      this.flashMandantSaveFeedback();
    } else {
      this.toastrService.error(res.message ?? $localize`:@@globalSaveFailedNoId:Das Speichern ist fehlgeschlagen. Bitte lade die Seite neu und versuche es erneut.`);
    }
    this.mandantService.getMandants().then(m => this.frontendService.fromArray(m))
  }

  /** Vergleicht `editMandant` mit dem beim Öffnen/letzten Speichern erfassten Stand — der
   * Speichern-Button soll erst aktiv werden, wenn tatsächlich etwas geändert wurde (nicht nur
   * gültig ist). `JSON.stringify` reicht hier: `editMandant` besteht nur aus Primitives und
   * `tiles` (ObjectId/String-Ids serialisieren via `toJSON()` gleich, egal welcher Typ). */
  private editMandantSnapshot?: string;

  public hasMandantChanges(): boolean {
    return !!this.editMandant && JSON.stringify(this.editMandant) !== this.editMandantSnapshot;
  }

  /** Kurzes Erfolgs-Feedback direkt am Speichern-Button, statt den Drawer zu schließen. */
  private flashMandantSaveFeedback(): void {
    this.mandantSaveFeedback = 'saved';
    this.cdr.detectChanges();
    setTimeout(() => {
      this.mandantSaveFeedback = 'idle';
      this.cdr.detectChanges();
    }, 1500);
  }

  public async deleteMandant(data: Mandant) {
    const res = await this.mandantService.deleteMandant(data._id);
    if (res.status == "ok") {
      this.toastrService.success($localize`:@@globalSaved:Gespeichert!`);
    }
    await this.closeEditMandant();
    this.cdr.detectChanges();
    this.mandantService.getMandants().then(m => this.frontendService.fromArray(m))
  }

  public async createMandant(data: any) {
    this.createMandantSaving = true;
    this.cdr.detectChanges();
    let res;
    try {
      res = await this.mandantService.createMandant(data.name, data.short, data.tiles, data.internal === true);
    } finally {
      this.createMandantSaving = false;
      this.cdr.detectChanges();
    }
    if (res.status == "ok") {
      this.flashCreateMandantSaveFeedback();
      this.mandantService.getMandants().then(m => this.frontendService.fromArray(m));
    } else {
      this.toastrService.error(res.message ?? $localize`:@@mandantCreateFailed:Der Mandant konnte nicht angelegt werden.`);
    }
  }

  /** Kurzes Erfolgs-Feedback direkt am Speichern-Button, dann schließt der Drawer - analog zu
   * flashMandantSaveFeedback() im Bearbeiten-Drawer. */
  private flashCreateMandantSaveFeedback(): void {
    this.createMandantSaveFeedback = 'saved';
    this.cdr.detectChanges();
    setTimeout(() => {
      this.createMandantSaveFeedback = 'idle';
      this.closeCreateMandant();
      this.cdr.detectChanges();
    }, 800);
  }

  ngAfterViewInit(): void {
    this.frontendService.results$.subscribe(r => {
      if (r && r.length > 0 && this.overlayService.get("selected")) {
        const found = r.find(u => u._id.toString() == this.overlayService.get("selected"));
        if (found == undefined) {
          void this.closeEditMandant();
          return;
        }
        // Drawer für diesen Mandanten bereits offen (z.B. Refetch nach Speichern) — nicht mit einem
        // frisch nachgeladenen Stand überschreiben, sonst kann die gerade gespeicherte Kachel-
        // Zuweisung durch noch nicht propagierte Serverdaten wieder zurückspringen.
        if (this.editVisible && this.editMandant?._id === found._id) {
          return;
        }
        this.editMandant = { ...found, tiles: [...found.tiles] };
        this.editMandantSnapshot = JSON.stringify(this.editMandant);
        this.editVisible = true;
        this.loadMandantUsers(this.editMandant);
      }
    })
  }

  public getTileById(tile: any): Tile | undefined {
    return this.tiles.find(element => element._id == tile)
  }

  /** Kacheln aus dem globalen Katalog, die noch nicht zugewiesen sind — Quelle fürs Dropdown.
   * Gecacht pro `data`-Objekt (WeakMap): `ng-select` rendert seine Optionsliste per `*ngFor` neu,
   * sobald sich die `[items]`-Array-Referenz ändert — bei jedem Change-Detection-Zyklus frisch zu
   * filtern hätte also bei JEDEM Tick (auch während eines Klicks aufs Dropdown) die Options-DOM-
   * Elemente ausgetauscht und so den Klick "verschluckt" (dasselbe Problem wie beim PickList,
   * siehe getPicklistState() in form.component.ts). Wird nur neu berechnet, wenn sich `this.tiles`
   * oder `data.tiles` selbst (Referenz) ändern. */
  private availableTilesCache = new WeakMap<object, { tilesRef: Tile[]; dataTilesRef: (string | ObjectId)[]; result: Tile[] }>();

  public getAvailableTilesToAdd(data: { tiles: (string | ObjectId)[] }): Tile[] {
    const cached = this.availableTilesCache.get(data);
    if (cached && cached.tilesRef === this.tiles && cached.dataTilesRef === data.tiles) {
      return cached.result;
    }
    const assigned = data.tiles.map(id => id.toString());
    const result = this.tiles.filter(t => !assigned.includes(t._id));
    this.availableTilesCache.set(data, { tilesRef: this.tiles, dataTilesRef: data.tiles, result });
    return result;
  }

  /** Die aktuell zugewiesenen Kacheln in voller Form — Quelle für die Liste. Gleiches Caching wie
   * getAvailableTilesToAdd(), aus demselben Grund (Angular *ngFor-Neurendering bei jeder neuen
   * Array-Referenz). */
  private assignedTilesCache = new WeakMap<object, { tilesRef: Tile[]; dataTilesRef: (string | ObjectId)[]; result: Tile[] }>();

  public getAssignedTiles(data: { tiles: (string | ObjectId)[] }): Tile[] {
    const cached = this.assignedTilesCache.get(data);
    if (cached && cached.tilesRef === this.tiles && cached.dataTilesRef === data.tiles) {
      return cached.result;
    }
    const result = data.tiles.map(id => this.getTileById(id.toString())).filter((t): t is Tile => !!t);
    this.assignedTilesCache.set(data, { tilesRef: this.tiles, dataTilesRef: data.tiles, result });
    return result;
  }


  public addTileToMandant(data: { tiles: (string | ObjectId)[] }, tileId: string | null): void {
    if (!tileId || data.tiles.some(id => id.toString() === tileId)) return;
    data.tiles = [...data.tiles, tileId];
  }

  public removeTileFromMandant(data: { tiles: (string | ObjectId)[] }, tileId: string): void {
    data.tiles = data.tiles.filter(id => id.toString() !== tileId);
  }

  /** Menü-Einträge fürs "+"-Dropdown (noch nicht zugewiesene Kacheln) — gecacht analog zu
   * getAvailableTilesToAdd(): `p-menu` ist ebenfalls OnPush, eine bei jedem Change-Detection-
   * Zyklus neu erzeugte `[model]`-Array-Referenz würde das offene Menü mitten im Klick neu
   * rendern. Wird nur neu gebaut, wenn sich die zugrundeliegende Verfügbar-Liste ändert.
   * `tile` transportiert das volle Objekt fürs Custom-Item-Template (Icon + Farbe). */
  private tileMenuItemsCache = new WeakMap<object, { sourceRef: Tile[]; items: TileMenuItem[] }>();

  public getTileMenuItems(data: { tiles: (string | ObjectId)[] }): TileMenuItem[] {
    const available = this.getAvailableTilesToAdd(data);
    const cached = this.tileMenuItemsCache.get(data);
    if (cached && cached.sourceRef === available) {
      return cached.items;
    }
    const items: TileMenuItem[] = available.map(tile => ({
      label: tile.name,
      tile,
      command: () => this.addTileToMandant(data, tile._id),
    }));
    this.tileMenuItemsCache.set(data, { sourceRef: available, items });
    return items;
  }


  /** Öffnet einen Mandanten in der Bearbeiten-Ansicht (Klick auf Tabellenzeile) — bearbeitet
   * bewusst eine Kopie, nicht die Tabellenzeile direkt: "Abbrechen"/anderweitiges Schließen des
   * Drawers muss unsaved Änderungen verwerfen können, siehe closeEditMandant(). */
  public selectMandantForEdit(mandant: Mandant): void {
    this.editMandant = { ...mandant, tiles: [...mandant.tiles] };
    this.editMandantSnapshot = JSON.stringify(this.editMandant);
    this.editVisible = true;
    this.mandantSaveFeedback = 'idle';
    this.selectedUserOption = undefined;
    this.newMandantUserName = '';
    this.loadMandantUsers(mandant);
    this.overlayService.set('selected', mandant._id.toString());
  }

  /** Schließt den Bearbeiten-Drawer (Abbruch, Löschen oder Deep-Link ohne Treffer). Awaited von
   * den Aufrufern, bevor die Mandantenliste neu geladen wird - sonst kann `results$` erneut
   * emittieren, während der `selected`-Query-Param in der URL noch auf den alten Mandanten zeigt
   * (die Navigation dafür ist async), und die Deep-Link-Subscription öffnet den Drawer sofort
   * wieder. */
  public async closeEditMandant(): Promise<void> {
    this.editingGlobalUserId = undefined;
    this.editVisible = false;
    this.editMandant = undefined;
    this.selectedMandant = undefined;
    this.mandantSaveFeedback = 'idle';
    this.mandantUsers = [];
    await this.overlayService.set('selected', undefined);
  }

  /** Lädt die Nutzer eines Mandanten neu - nach dem Öffnen des Drawers sowie nach jedem
   * Hinzufügen/Entfernen, damit die Liste immer den Live-Stand zeigt. */
  private loadMandantUsers(mandant: Mandant): void {
    this.userService.getMandantUsersAsReflactAdmin(mandant._id.toString()).then(users => {
      this.mandantUsers = users;
      this.cdr.detectChanges();
    });
  }

  /** Entfernt `user` aus `mandant` (nicht global) - siehe user.router.ts#deleteAsReflactAdmin. */
  public async removeUserFromMandant(mandant: Mandant, user: FullUser): Promise<void> {
    const res = await this.userService.deleteMandantUserAsReflactAdmin(user.globalUserId, mandant._id.toString());
    if (res.status === 'ok') {
      this.mandantUsers = this.mandantUsers.filter(u => u.globalUserId.toString() !== user.globalUserId.toString());
    } else {
      this.toastrService.error(res.message ?? $localize`:@@mandantRemoveUserFailed:Der Nutzer konnte nicht entfernt werden.`);
    }
    this.cdr.detectChanges();
  }

  /** Fügt den in `selectedUserOption` gewählten/eingegebenen Nutzer zu `mandant` hinzu - bei einem
   * BESTEHENDEN Nutzer (`isNew: false`) ist der Name bereits bekannt und wird nur der Organisation
   * hinzugefügt; bei einem neu eingegebenen (`isNew: true`) legt `createUserAsReflactAdmin` einen
   * komplett neuen Nutzer direkt in diesem Mandanten an (siehe server/src/user/user.service.ts#
   * createUser). Berechtigungen können danach in der globalen Nutzerverwaltung gepflegt werden. */
  public async addUserToMandant(mandant: Mandant): Promise<void> {
    const option = this.selectedUserOption;
    if (!option) return;
    const name = option.isNew ? this.newMandantUserName.trim() : option.name;
    if (!name) return;
    const res = await this.userService.createUserAsReflactAdmin(option.email, name, [], mandant._id.toString(), false, []);
    if (res.status === 'ok') {
      this.toastrService.success($localize`:@@globalSaved:Gespeichert!`);
      if (option.isNew) {
        this.userOptions = [...this.userOptions, { ...option, name, isNew: false }];
      }
      this.selectedUserOption = undefined;
      this.newMandantUserName = '';
      this.loadMandantUsers(mandant);
    } else {
      this.toastrService.error(res.message ?? $localize`:@@mandantAddUserFailed:Der Nutzer konnte nicht hinzugefügt werden.`);
    }
    // Async-Fortsetzung nach `await` löst hier keine automatische Change Detection aus (bekanntes
    // Muster in dieser App) - ohne detectChanges() blieben die Eingabefelder optisch gefüllt.
    this.cdr.detectChanges();
  }

  public open() {
    this.newMandant = {
      "name": "",
      "short": "",
      "tiles": [],
      "internal": false
    }
    this.createMandantSaveFeedback = 'idle';
    this.createVisible = true;
  }

  /** Schließt den "Neuer Mandant"-Drawer (Abbruch oder nach erfolgreichem Anlegen). */
  public closeCreateMandant(): void {
    this.createVisible = false;
    this.newMandant = undefined;
  }

  /** Spaltenbeschriftung der p-table — nutzt dieselben Übersetzungen wie das Bearbeiten-/Erstellen-Formular. */
  public getColumnLabel(fieldName: string): string {
    return this.translatedLabels.get(fieldName) ?? fieldName;
  }
}
